import { useUrlSearchParams } from '@dai/common'
import {
  Check,
  DarkMode,
  ExpandLess,
  ExpandMore,
  LightMode,
  Settings,
  Translate,
} from '@mui/icons-material'
import {
  Box,
  Collapse,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material'
import { useState } from 'react'

import { THEMES } from '@/constants'
import { useRemoteConfig } from '@/contexts/RemoteConfigContext'
import { usePermissions } from '@/hooks/usePermissions'
import useTheme from '@/hooks/useTheme'
import {
  Language as LanguageType,
  LanguageManager,
  tolgee,
} from '@/i18n/tolgee'
import useT from '@/i18n/useT'

import { Colors } from './Colors'

function SettingsButton() {
  const [anchorMenu, setAnchorMenu] = useState<any>(null)
  const [languageOpen, setLanguageOpen] = useState(false)

  const toggleMenu = (event: React.SyntheticEvent) => {
    setAnchorMenu(event.currentTarget)
  }

  const closeMenu = () => {
    setAnchorMenu(null)
  }
  const { t } = useT()
  const {
    handle: { handleNavTo },
  } = useUrlSearchParams()
  const { theme, setTheme } = useTheme()
  const { permissions } = usePermissions()
  const darkMode = theme === THEMES.DARK
  const color = darkMode ? Colors.WHITE : Colors.BLACK

  const { enablePayments } = useRemoteConfig()

  const handleThemeToggle = () => {
    const newTheme = darkMode ? THEMES.LIGHT : THEMES.DARK
    setTheme(newTheme)
  }

  const handleGoToUsers = () => {
    handleNavTo('/admin/users', { resetParams: false })
    closeMenu()
  }

  const handleGoToPlanManagementAndBilling = () => {
    handleNavTo('/admin/plan-management', { resetParams: false })
    closeMenu()
  }

  const handleGoToSalesData = () => {
    handleNavTo('/admin/sales-data', { resetParams: false })
    closeMenu()
  }

  const handleGoToStoreAssignments = () => {
    handleNavTo('/admin/store-assignments', { resetParams: false })
    closeMenu()
  }

  const handleChangeLanguage = (lang: LanguageType) => {
    LanguageManager.setCurrentLanguage(lang)
    closeMenu()
  }

  return (
    <>
      <IconButton onClick={toggleMenu} id={'settings-dropdown'}>
        <Tooltip title={t('navbar.settings.goTo')}>
          <Settings style={{ color }} />
        </Tooltip>
      </IconButton>
      <Menu
        id="menu-admin"
        anchorEl={anchorMenu}
        open={Boolean(anchorMenu)}
        onClose={closeMenu}
        MenuListProps={{ sx: { py: 1.0 } }}
      >
        <MenuItem onClick={handleGoToUsers}>
          {t('navbarUserDropdown.users')}
        </MenuItem>
        {permissions.CAN_MANAGE_BILLING && enablePayments && (
          <>
            <Divider />
            <MenuItem onClick={handleGoToPlanManagementAndBilling}>
              {t('navbarUserDropdown.planManagementAndBilling')}
            </MenuItem>
          </>
        )}
        <>
          <Divider />
          <MenuItem onClick={handleGoToStoreAssignments}>
            {t('navbarUserDropdown.storeAssignments')}
          </MenuItem>
        </>
        <Divider />
        <MenuItem onClick={handleGoToSalesData}>
          {t('navbarUserDropdown.salesData')}
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={handleThemeToggle}
          sx={{ justifyContent: 'space-between', gap: 4 }}
        >
          <Typography variant="body2">
            {darkMode ? t('common.lightMode') : t('common.darkMode')}
          </Typography>
          {darkMode ? (
            <LightMode fontSize="small" />
          ) : (
            <DarkMode fontSize="small" />
          )}
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => setLanguageOpen(o => !o)}
          sx={{ justifyContent: 'space-between', gap: 4 }}
        >
          <Typography variant="body2">{t('common.language')}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Translate fontSize="small" />
            {languageOpen ? (
              <ExpandLess fontSize="small" />
            ) : (
              <ExpandMore fontSize="small" />
            )}
          </Box>
        </MenuItem>
        <Collapse in={languageOpen}>
          <Divider />
          <MenuItem onClick={() => handleChangeLanguage('en')} sx={{ pl: 6 }}>
            <Typography variant="body2">{t('common.english')}</Typography>
            {tolgee.getLanguage() === 'en' && (
              <Check fontSize="small" sx={{ marginLeft: 2 }} />
            )}
          </MenuItem>
          <MenuItem onClick={() => handleChangeLanguage('es')} sx={{ pl: 6 }}>
            <Typography variant="body2">{t('common.spanish')}</Typography>
            {tolgee.getLanguage() === 'es' && (
              <Check fontSize="small" sx={{ marginLeft: 2 }} />
            )}
          </MenuItem>
        </Collapse>
      </Menu>
    </>
  )
}

export default SettingsButton
